// @flow
import React from 'react';
import {graphql} from 'react-apollo';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {ErrorReport, SuccessReport} from '@homeaway/react-error-reporter';
import seoMetaDataQuery from '../../queries/seoMetaDataQuery';
import {isSearchRoot} from '../../../server/utils/canonical';
import {pick} from 'lodash';

import type {ComponentType} from 'react';
import type {SearchQueryResults} from '../../queries/searchQuery';
import type {SearchParamsType} from '../../reducers/searchParams';
import type {State as ReduxState} from '../../reducers';
import type {
    SeoMetaData,
    SeoMetaDataResponseData
} from '../../queries/seoMetaDataQuery';

/**
 * @type{Object}
 * Is using in seoMetaDataQueryOptions function that generate params for seoMetaData query.
 */
type SeoMetaDataParams = {
    searchParams: SearchParamsType,
    results: ?SearchQueryResults,
    pathname: string
};

type StateProps = {
    gtsQuery: boolean,
    searchParams: SearchParamsType,
    pathname: string
};

type InputProps = {
    results: ?SearchQueryResults,
    resultsLoading: boolean
};

type GraphQLProps = {
    seoDataResponse: SeoMetaDataResponseData
};

type ConnectorProps = InputProps & StateProps & GraphQLProps;

/**
 * @type{Object}
 * Type to be used by components applying this HOC. Describes the injected
 * `seoData` prop.
 */
export type WithSeoMetaDataProps = {
    seoData: SeoMetaData
};

const paramsToLog = [
    'isBoundingBoxSearch',
    'legacyRegionPath',
    'pinnedPropertyListing',
    'q',
    'regionId',
    'searchTermUuid'
];

const systemDataToLog = ['isGoodBot', 'siteName', 'domainName'];

// TODO: [TRAVOPS-1135] remove pathname upon adding "referer" to headers whitelist
// See https://jira.homeawaycorp.com/browse/TRAVOPS-1135
const logDataPropWhitelist = [...systemDataToLog, 'pathname'];

const generateSeoMetaDataParam = ({
    searchParams = {},
    results
}: SeoMetaDataParams) => ({
    filter: searchParams.filter,
    petIncluded: searchParams.petIncluded,
    currency: searchParams.currency,
    lastBreadcrumbNode:
        results && results.geography && results.geography.name
            ? results.geography.name
            : '',
    pageSize: results ? results.pageSize : '',
    page: results ? results.page : '',
    hitCount: results ? results.resultCount : '',
    adultsCount: searchParams.adultsCount || 0,
    childrenCount: searchParams.childrenCount || 0,
    maxBathrooms: searchParams.maxBathrooms,
    maxBedrooms: searchParams.maxBedrooms,
    minBathrooms: searchParams.minBathrooms,
    minBedrooms: searchParams.minBedrooms,
    maxNightlyPrice: searchParams.maxPrice,
    minNightlyPrice: searchParams.minPrice,
    lbsUuid:
        results && results.geography && results.geography.lbsId
            ? results.geography.lbsId
            : ''
});

export const seoMetaDataQueryOptions = (props: SeoMetaDataParams) => ({
    variables: {
        args: generateSeoMetaDataParam(props),
        isSearchRoot: isSearchRoot(props.pathname)
    },
    errorPolicy: 'all'
});

export const mapStateToProps = ({
    system,
    searchParams
}: ReduxState): StateProps => ({
    gtsQuery: system.gtsQuery,
    searchParams: searchParams.params,
    pathname: searchParams.pathname || '',
    ...pick(system, systemDataToLog)
});

function pickRequiredSeoFields(seoMetaData) {
    const {
        metaKeywords,
        pageTitle,
        metaDescription,
        headlineTitle,
        searchReviewsHeadline,
        canonicalLink
    } = seoMetaData;
    return {
        metaKeywords,
        pageTitle,
        metaDescription,
        headlineTitle,
        searchReviewsHeadline,
        canonicalLink
    };
}

function validateSeoMetaData(seoMetaData) {
    if (!seoMetaData) {
        return ['seoMetaData missing'];
    }
    return Object.keys(pickRequiredSeoFields(seoMetaData))
        .filter((key) => !seoMetaData[key])
        .map((key) => `${key} is missing`);
}

const skip = (props: StateProps & InputProps) =>
    !props.results || props.resultsLoading || !props.gtsQuery;

const commonLogData = (props) => ({
    ...pick(props, logDataPropWhitelist),
    ...pick(props.searchParams, paramsToLog)
});

// typed for flow 0.83 via
// https://github.com/facebook/flow/blob/91e85b8457671fe6c781873e37ae317e1402c830/website/en/docs/react/hoc.md
// doesn't matter because the types don't get enforced outside :/
export default function withSeoMetaData<Props: {}>(
    WrappedComponent: ComponentType<Props>
): ComponentType<$Diff<Props, {seoData: SeoMetaData | void}>> {
    const SeoDataComponent = (props: Props & ConnectorProps) => {
        const {seoDataResponse, results, ...rest} = props;
        const {loading, error, seoMetaData} = seoDataResponse || {};
        const {
            metaKeywords,
            pageTitle,
            metaDescription,
            headlineTitle,
            searchReviewsHeadline,
            canonicalLink,
            alternativeLinks
        } = results || {};

        const srsFallback = (
            <WrappedComponent
                {...rest}
                seoData={{
                    metaKeywords,
                    pageTitle,
                    metaDescription,
                    headlineTitle,
                    searchReviewsHeadline,
                    canonicalLink,
                    alternativeLinks
                }}
            />
        );

        // Exclude loading state or when GTS query is disabled from error reporting
        if (loading || skip(props)) {
            return srsFallback;
        }

        if (error) {
            return (
                <ErrorReport
                    name="Failed SEO data fetch"
                    logData={{
                        error: {
                            message: error.message,
                            type: error.constructor.name
                        },
                        ...commonLogData(props)
                    }}
                >
                    {srsFallback}
                </ErrorReport>
            );
        }

        const seoMetaDataErrors = validateSeoMetaData(seoMetaData);
        if (seoMetaDataErrors.length) {
            return (
                <ErrorReport
                    name="Missing SEO data"
                    logData={{
                        seoMetaDataErrors,
                        ...commonLogData(props)
                    }}
                >
                    {srsFallback}
                </ErrorReport>
            );
        }

        return (
            <SuccessReport name="SEO data fetched">
                <WrappedComponent {...rest} seoData={seoMetaData} />
            </SuccessReport>
        );
    };

    return compose(
        connect(mapStateToProps),
        graphql(seoMetaDataQuery, {
            options: ({
                searchParams,
                results,
                pathname
            }: StateProps & InputProps) =>
                seoMetaDataQueryOptions({
                    searchParams,
                    results,
                    pathname
                }),
            skip,
            name: 'seoDataResponse'
        })
    )(SeoDataComponent);
}
